/* eslint-disable max-lines, max-len */
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { DesktopDatePicker } from '@mui/x-date-pickers/DesktopDatePicker';
import { format, isValid } from 'date-fns';
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';
import { useState } from 'react';
import { useDispatch } from 'react-redux';

import { handleError } from 'store/thunks/errorHandlerThunks';
import { manualInventoryAdjustApi } from 'api/inventory';
import UNVERIFIED_REASON_OPTIONS from 'common/constants/unverifiedReasons';
import SYSTEM_DEFICIT from 'common/constants/inventoryLabels';
import RemoveReasonRows from './RemoveReasonRows';

const MAX_ADJUSTMENT_QTY = 9999;

// ─── Accordion section ─────────────────────────────────────────────────────────

const AccordionSection = ({
  expanded,
  onToggle,
  icon,
  title,
  subtitle,
  accentColor,
  status,
  children,
}) => {
  let statusChipEl = null;
  if (status === 'complete') {
    statusChipEl = (
      <Chip
        label='Completed'
        color='success'
        sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700, '& .MuiChip-label': { px: 0.75 } }}
      />
    );
  } else if (status === 'incomplete') {
    statusChipEl = (
      <Chip
        label='Incomplete'
        variant='outlined'
        sx={{
          height: 16,
          fontSize: '0.6rem',
          fontWeight: 600,
          color: 'warning.dark',
          borderColor: 'warning.light',
          bgcolor: '#fffbf0',
          '& .MuiChip-label': { px: 0.75 },
        }}
      />
    );
  }

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderLeft: `3px solid ${accentColor}`,
        borderRadius: 1,
        overflow: 'hidden',
      }}
    >
      <Box
        onClick={onToggle}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          px: 1.5,
          py: 1.25,
          cursor: 'pointer',
          userSelect: 'none',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        {icon}
        <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
        <Typography variant='caption' color='text.secondary'>
          {subtitle}
        </Typography>
        <Box sx={{ ml: 'auto', display: 'flex', alignItems: 'center', gap: 0.75 }}>
          {statusChipEl}
          {expanded ? (
            <ExpandLessIcon fontSize='small' sx={{ color: 'text.secondary' }} />
          ) : (
            <ExpandMoreIcon fontSize='small' sx={{ color: 'text.secondary' }} />
          )}
        </Box>
      </Box>
      {expanded && (
        <Box sx={{ px: 1.5, pb: 1.5 }}>
          <Divider sx={{ mb: 1.5 }} />
          {children}
        </Box>
      )}
    </Box>
  );
};
AccordionSection.propTypes = {
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  icon: PropTypes.node.isRequired,
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  accentColor: PropTypes.string.isRequired,
  status: PropTypes.oneOf(['complete', 'incomplete', null]),
  children: PropTypes.node.isRequired,
};
AccordionSection.defaultProps = { status: null };

// ─── Main dialog ───────────────────────────────────────────────────────────────

const getDayColor = (lot) => {
  if (lot?.isExpired) return 'error.main';
  if (lot?.isExpiringSoon) return 'warning.main';
  return 'text.primary';
};

const ManualAdjustmentDialog = ({
  open,
  lot,
  drugName,
  strength,
  ndc,
  fulfillingPharmacyId,
  facilityName,
  onSaveSuccess,
  onClose,
}) => {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  const [addExpanded, setAddExpanded] = useState(false);
  const [removeExpanded, setRemoveExpanded] = useState(false);
  const [howItWorksAnchor, setHowItWorksAnchor] = useState(null);

  const [addQty, setAddQty] = useState('');
  const [addNotes, setAddNotes] = useState('');
  const [removeTotal, setRemoveTotal] = useState('');
  const [removeReasons, setRemoveReasons] = useState([{ qty: '', reasons: [] }]);
  const [removeNotes, setRemoveNotes] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lockedError, setLockedError] = useState(false);
  // POM-6111 — only used when this lot carries an unverified balance, or has no BUD.
  const [unverifiedReason, setUnverifiedReason] = useState('');
  const [suppliedBud, setSuppliedBud] = useState(null);

  if (!lot) return null;

  const wipQty = lot.wipQty ?? 0;
  const availableQty = lot.availableQty ?? 0;

  const addQtyNum = parseFloat(addQty) || 0;
  const removeTotalNum = parseFloat(removeTotal) || 0;
  const reasonsSum = removeReasons.reduce((sum, r) => sum + (parseFloat(r.qty) || 0), 0);

  const addQtyIsDecimal = addQty !== '' && addQtyNum % 1 !== 0;
  const removeTotalIsDecimal = removeTotal !== '' && removeTotalNum % 1 !== 0;

  // POM-6111 — the lot's unverified (negative) balance, and what the admin has said about it.
  // Declared here rather than beside the other reconciliation gates below because the removal
  // bound now depends on it.
  const unverifiedQty = Number(lot.unverifiedQty) || 0;
  const needsUnverifiedReason = unverifiedQty > 0;
  // POM-6144 — these three codes say the claimed stock was never real, so they close the claim
  // WITHOUT crediting the shelf. Everything else says it turned out to be real. Core keeps the
  // same list in WRITE_OFF_REASON_CODES and rejects the combinations this hides.
  const isWriteOffReason = ['NEVER_EXISTED', 'BREAKAGE', 'DAMAGE'].includes(unverifiedReason);
  // Offered only where it can mean something: a claim to settle, and a reason that credits stock.
  const canSettleOnAdd = needsUnverifiedReason && !isWriteOffReason;
  // How much of the addition is taken to have been already dispensed against the claim.
  //
  // DERIVED, not entered. The field is shown read-only for now, so this is always the whole
  // claim the addition can cover: a missed receipt covering it is far and away the common case,
  // and letting an operator lower it needs the "separate delivery to a claimed lot" case
  // thought through first (leave it at 0 and the claim stays open for a later correction).
  //
  // Deliberately still SENT, so core's settlement path is unchanged and the arithmetic stays
  // exercised in production. Re-enabling is putting the input back, not rewiring the payload.
  const settledQtyNum = Math.min(addQtyNum, unverifiedQty);

  const addActive = addQtyNum > 0;
  const removeActive = removeTotalNum > 0;
  const addValid =
    addActive && !addQtyIsDecimal && addQtyNum <= MAX_ADJUSTMENT_QTY && addNotes.trim().length > 0;
  // POM-6144 — a write-off is bounded by the CLAIM, not by the shelf. A claimed lot normally
  // sits at availableQty 0, so the ordinary bound would reject every write-off outright — the
  // exact case these reason codes exist for.
  const removeCap = isWriteOffReason ? unverifiedQty : availableQty;
  const removeValid =
    removeActive &&
    !removeTotalIsDecimal &&
    removeTotalNum <= removeCap &&
    reasonsSum === removeTotalNum &&
    removeReasons.every((r) => parseFloat(r.qty) > 0 && r.reasons.length > 0);

  // POM-6111 — the remaining reconciliation gate. A reason CODE is required when the lot carries
  // an unverified balance, because that is what the audit report groups on to answer why the
  // correction was permitted; free-text notes cannot be grouped. A BUD is required when the lot
  // has none — a lot created by a fulfilment claim has no expiration date, because one cannot be
  // read from a barcode, and it stays non-dispensable until somebody supplies it. An adjustment is
  // one of the admin moments where that can be put right.
  const needsBud = !lot.expirationDate;
  const suppliedBudValid = isValid(suppliedBud);
  const reconcileValid =
    (!needsUnverifiedReason || unverifiedReason !== '') && (!needsBud || suppliedBudValid);

  const canSave =
    (addActive || removeActive) &&
    (!addActive || addValid) &&
    (!removeActive || removeValid) &&
    reconcileValid;

  const showBothWarning = addActive && removeActive;

  let addStatus = null;
  if (addActive) addStatus = addValid ? 'complete' : 'incomplete';

  let removeStatus = null;
  if (removeActive) removeStatus = removeValid ? 'complete' : 'incomplete';

  const budFormatted = lot.expirationDate
    ? format(new Date(lot.expirationDate), 'MMM dd, yyyy')
    : '—';

  const removeQtyOverLimit = removeActive && removeTotalNum > availableQty;
  let addHelperText = `Adds to current stock of ${lot.remainingQty}`;
  if (addQtyIsDecimal) addHelperText = 'Whole numbers only';
  else if (addQtyNum > MAX_ADJUSTMENT_QTY)
    addHelperText = `Max single adjustment: ${MAX_ADJUSTMENT_QTY}`;
  let removeHelperText = `Max: ${availableQty} (excl. ${wipQty} WIP)`;
  if (removeQtyOverLimit)
    removeHelperText = `Max ${availableQty} — ${wipQty} units reserved as WIP`;
  if (removeTotalIsDecimal) removeHelperText = 'Whole numbers only';

  const handleClose = () => {
    setAddExpanded(false);
    setRemoveExpanded(false);
    setAddQty('');
    setAddNotes('');
    setRemoveTotal('');
    setRemoveReasons([{ qty: '', reasons: [] }]);
    setRemoveNotes('');
    setSubmitted(false);
    setLockedError(false);
    setUnverifiedReason('');
    setSuppliedBud(null);
    onClose();
  };

  const handleClearAdd = () => {
    setAddQty('');
    setAddNotes('');
    // Nothing to reset for the settled figure: it is derived from the quantity, so clearing the
    // quantity clears it too.
  };

  const handleClearRemove = () => {
    setRemoveTotal('');
    setRemoveReasons([{ qty: '', reasons: [] }]);
    setRemoveNotes('');
  };

  const handleSave = () => {
    setSubmitted(true);
    if (!canSave) return;
    setLockedError(false);
    setSaving(true);
    const adjustItems = [];
    const lotBase = {
      ndc,
      fulfillingPharmacyId,
      inventoryLotId: lot?.inventoryLotId,
      lotNumber: lot?.lotNumber,
      // POM-6111 — `reasonCode` is a GraphQL ENUM, so a blank string is a request-level
      // validation error rather than a null: it has to be omitted, not blanked.
      ...(needsUnverifiedReason && unverifiedReason ? { reasonCode: unverifiedReason } : {}),
      ...(needsBud && suppliedBudValid
        ? { expirationDate: format(suppliedBud, 'yyyy-MM-dd') }
        : {}),
    };
    if (addValid) {
      adjustItems.push({
        ...lotBase,
        direction: 'ADJUST_IN',
        quantity: addQtyNum,
        notes: addNotes,
        // POM-6144 — omitted rather than sent as 0 when there is nothing to settle, so an
        // ordinary addition's payload is unchanged. Core credits the full quantity and then
        // takes this much straight back off both the lot and the claim.
        ...(canSettleOnAdd && settledQtyNum > 0 ? { settledQty: settledQtyNum } : {}),
      });
    }
    if (removeValid) {
      adjustItems.push({
        ...lotBase,
        direction: 'ADJUST_OUT',
        quantity: removeTotalNum,
        notes: removeNotes || undefined,
        offageReasons: removeReasons.map((r) => ({ qty: parseFloat(r.qty), reasons: r.reasons })),
      });
    }
    const { lotNumber } = lot;
    manualInventoryAdjustApi(adjustItems)
      .then(() => {
        setSaving(false);
        handleClose();
        onSaveSuccess();
        enqueueSnackbar(`Inventory adjustment saved for ${lotNumber}`, { variant: 'success' });
      })
      .catch((error) => {
        setSaving(false);
        const errorCode = error?.response?.errors?.[0]?.extensions?.code;
        if (
          errorCode === 'ITEM_LOCKED_FOR_PHYSICAL_COUNT' ||
          error?.message?.toLowerCase().includes('locked for physical count')
        ) {
          setLockedError(true);
        } else {
          dispatch(handleError({ error }));
        }
      });
  };

  let statusChip;
  if (lot.isExpired) {
    statusChip = (
      <Chip
        label='Expired'
        color='error'
        sx={{ height: 16, fontSize: '0.6rem', fontWeight: 600, '& .MuiChip-label': { px: 0.75 } }}
      />
    );
  } else if (lot.isExpiringSoon) {
    statusChip = (
      <Chip
        label='Expiring Soon'
        sx={{
          height: 16,
          fontSize: '0.6rem',
          fontWeight: 600,
          bgcolor: '#FFC107',
          color: '#212121',
          '& .MuiChip-label': { px: 0.75 },
        }}
      />
    );
  } else {
    statusChip = (
      <Chip
        label='Active'
        color='success'
        sx={{
          height: 16,
          fontSize: '0.6rem',
          fontWeight: 600,
          color: '#fff',
          '& .MuiChip-label': { px: 0.75 },
        }}
      />
    );
  }

  return (
    <Dialog
      open={open}
      // Closing discards the whole adjustment (handleClose resets qty, notes and reasons), so an
      // accidental backdrop click or Escape must not dismiss it — closing is only via the explicit
      // close icon or Cancel button.
      onClose={(event, reason) => {
        if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
        handleClose();
      }}
      disableEscapeKeyDown
      PaperProps={{ sx: { width: 640, maxWidth: 640 } }}
    >
      {/* ── Header ── */}
      <Box
        sx={{
          px: 2.5,
          pt: 2,
          pb: 1.25,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
        }}
      >
        <Typography variant='h6' sx={{ fontWeight: 700, lineHeight: 1.2 }}>
          Manual Inventory Adjustment
        </Typography>
        <IconButton onClick={handleClose} size='small' sx={{ mt: -0.25, ml: 1 }}>
          <CloseIcon fontSize='small' />
        </IconButton>
      </Box>

      {/* ── Drug Identity Strip ── */}
      <Box sx={{ px: 2.5, pb: 1.5 }}>
        <Typography
          variant='subtitle1'
          sx={{ fontWeight: 700, lineHeight: 1.4, color: 'text.primary' }}
        >
          {drugName}
          {strength ? ` ${strength}` : ''}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
          <Typography variant='body2' color='text.secondary'>
            NDC :
          </Typography>
          <Typography variant='body2' sx={{ fontFamily: 'monospace' }}>
            {ndc}
          </Typography>
          <Typography variant='body2' color='text.disabled' sx={{ mx: 0.5 }}>
            |
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Facility :
          </Typography>
          <Typography variant='body2'>{facilityName}</Typography>
        </Box>
      </Box>

      <Divider />

      {/* ── Lot Info ── */}
      <Box sx={{ px: 2.5, pt: 2, pb: 1 }}>
        <Box
          sx={{
            bgcolor: '#f4f6f8',
            borderRadius: 1,
            borderLeft: '4px solid #0d1f35',
            px: 2,
            py: 1.5,
          }}
        >
          {/* Row 1: LOT #, BUD, REMAINING, WIP */}
          <Box sx={{ display: 'flex', gap: 0, mb: 1.5 }}>
            {[
              {
                label: 'LOT #',
                node: (
                  <Typography variant='body2' sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                    {lot.lotNumber}
                  </Typography>
                ),
              },
              {
                label: 'BUD',
                node: (
                  <Typography
                    variant='body2'
                    sx={{ fontWeight: 700, color: lot.isExpiringSoon ? '#e65100' : 'text.primary' }}
                  >
                    {budFormatted}
                  </Typography>
                ),
              },
              {
                label: 'REMAINING',
                node: (
                  <Typography variant='body2' sx={{ fontWeight: 700 }}>
                    {lot.remainingQty}{' '}
                    <Typography component='span' variant='caption' color='text.secondary'>
                      units
                    </Typography>
                  </Typography>
                ),
              },
              {
                label: 'WIP',
                node: (
                  <Typography variant='body2' sx={{ fontWeight: 700 }}>
                    {wipQty}{' '}
                    <Typography component='span' variant='caption' color='text.secondary'>
                      units
                    </Typography>
                  </Typography>
                ),
              },
            ].map(({ label, node }) => (
              <Box key={label} sx={{ flex: 1 }}>
                <Typography
                  variant='caption'
                  sx={{ color: 'text.secondary', fontSize: 10, display: 'block', mb: 0.25 }}
                >
                  {label}
                </Typography>
                {node}
              </Box>
            ))}
          </Box>

          {/* Row 2: DAYS TO EXPIRY, AVAILABLE QTY, status chip */}
          <Box sx={{ display: 'flex', gap: 0, alignItems: 'flex-end' }}>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant='caption'
                sx={{ color: 'text.secondary', fontSize: 10, display: 'block', mb: 0.25 }}
              >
                DAYS TO EXPIRY
              </Typography>
              <Typography variant='body2' sx={{ fontWeight: 700, color: getDayColor(lot) }}>
                {lot.daysUntilExpiry}d
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant='caption'
                sx={{ color: 'text.secondary', fontSize: 10, display: 'block', mb: 0.25 }}
              >
                AVAILABLE QTY
              </Typography>
              <Typography variant='body2' sx={{ fontWeight: 700, color: 'success.main' }}>
                {availableQty}{' '}
                <Typography component='span' variant='caption' color='text.secondary'>
                  units
                </Typography>
              </Typography>
            </Box>
            <Box sx={{ flex: 1 }}>
              <Typography
                variant='caption'
                sx={{ color: 'text.secondary', fontSize: 10, display: 'block', mb: 0.25 }}
              >
                STATUS
              </Typography>
              {statusChip}
            </Box>
            {/* POM-6111 — the unverified balance belongs in the identity card, not only in the
                reconcile prompt below: it is a standing fact about the lot, and the prompt
                disappears the moment a reason is picked. Shown as a NEGATIVE magnitude, matching
                the Lot Summary drawer and the count dialog, because it is quantity the pharmacy
                is short — and deliberately never summed with REMAINING, which is the whole point
                of keeping the two buckets apart. Falls back to the original spacer so row 2 stays
                aligned with row 1's four columns. */}
            {unverifiedQty > 0 ? (
              <Box sx={{ flex: 1 }}>
                <Typography
                  variant='caption'
                  sx={{ color: 'text.secondary', fontSize: 10, display: 'block', mb: 0.25 }}
                >
                  {SYSTEM_DEFICIT.toUpperCase()}
                </Typography>
                <Typography
                  variant='body2'
                  sx={{ fontWeight: 700, color: '#e65100' }}
                  data-testid='adjust-unverified-qty'
                >
                  {unverifiedQty}{' '}
                  <Typography component='span' variant='caption' color='text.secondary'>
                    units
                  </Typography>
                </Typography>
              </Box>
            ) : (
              <Box sx={{ flex: 1 }} />
            )}
          </Box>
        </Box>
      </Box>

      <Divider sx={{ mt: 1 }} />

      {/* ── Action sections ── */}
      <Box sx={{ px: 2.5, py: 2, display: 'flex', flexDirection: 'column', gap: 1.25 }}>
        {lockedError && (
          <Alert severity='warning' sx={{ mb: 2 }}>
            Inventory item is locked for physical count. Adjustments are not allowed until the count
            is complete.
          </Alert>
        )}
        {/* ── POM-6111: reconciliation gates ──
            Rendered ABOVE the action sections, and only when they apply, because both block the
            save in either direction — the admin should meet them before typing a quantity rather
            than discovering a disabled button afterwards. */}
        {(needsUnverifiedReason || needsBud) && (
          <Alert severity='warning' icon={false} sx={{ mb: 0.5 }}>
            <Stack spacing={1.5}>
              <Box>
                <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
                  This lot needs reconciling
                </Typography>
                {needsUnverifiedReason && (
                  <Typography
                    variant='caption'
                    display='block'
                    data-testid='adjust-unverified-note'
                  >
                    {unverifiedQty} unit{unverifiedQty === 1 ? '' : 's'} were dispensed from this
                    lot before inventory ever received it. Say why before adjusting it.
                  </Typography>
                )}
                {needsBud && (
                  <Typography variant='caption' display='block' data-testid='adjust-bud-note'>
                    This lot has no expiration date, so it cannot be dispensed. Supply the date from
                    the packaging.
                  </Typography>
                )}
              </Box>

              {needsUnverifiedReason && (
                <FormControl
                  size='small'
                  fullWidth
                  error={submitted && unverifiedReason === ''}
                  sx={{ bgcolor: 'background.paper' }}
                >
                  <InputLabel id='adjust-unverified-reason-label'>Reason *</InputLabel>
                  <Select
                    labelId='adjust-unverified-reason-label'
                    label='Reason *'
                    value={unverifiedReason}
                    onChange={(e) => setUnverifiedReason(e.target.value)}
                    inputProps={{ 'data-testid': 'adjust-unverified-reason' }}
                  >
                    {UNVERIFIED_REASON_OPTIONS.map((option) => (
                      <MenuItem key={option.code} value={option.code}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {submitted && unverifiedReason === '' && (
                    <FormHelperText>Required</FormHelperText>
                  )}
                </FormControl>
              )}

              {needsBud && (
                <DesktopDatePicker
                  label='Beyond-Use Date *'
                  inputFormat='MM/dd/yyyy'
                  value={suppliedBud}
                  onChange={setSuppliedBud}
                  renderInput={(params) => (
                    <TextField
                      // eslint-disable-next-line react/jsx-props-no-spreading
                      {...params}
                      size='small'
                      fullWidth
                      sx={{ bgcolor: 'background.paper' }}
                      error={submitted && !suppliedBudValid}
                      helperText={submitted && !suppliedBudValid ? 'Required' : ''}
                      inputProps={{
                        ...params.inputProps,
                        'data-testid': 'adjust-supplied-bud',
                      }}
                    />
                  )}
                />
              )}
            </Stack>
          </Alert>
        )}

        <Typography variant='caption' sx={{ color: 'warning.dark', fontWeight: 500 }}>
          Choose an action to record for this lot.
        </Typography>

        {/* Add Stock accordion */}
        <AccordionSection
          expanded={addExpanded}
          onToggle={() => setAddExpanded((prev) => !prev)}
          icon={
            <AddCircleOutlineIcon sx={{ color: 'success.main', fontSize: 20, flexShrink: 0 }} />
          }
          title='Add Stock'
          subtitle='increase remaining qty'
          accentColor='#2e7d32'
          status={addStatus}
        >
          {/* Side by side, because the two numbers are read together: the second is a
              breakdown OF the first, not a separate decision. Stacked, the settled figure read
              as another quantity to supply. */}
          <Box
            sx={{ display: 'flex', gap: 1.5, alignItems: 'flex-start', mb: 1.5 }}
            data-testid='adjust-add-row'
          >
            <TextField
              type='number'
              label='Qty to Add *'
              size='small'
              sx={{ width: 180 }}
              value={addQty}
              onChange={(e) => setAddQty(e.target.value)}
              inputProps={{ min: 1, max: MAX_ADJUSTMENT_QTY, step: 1 }}
              error={addQtyIsDecimal || addQtyNum > MAX_ADJUSTMENT_QTY}
              helperText={addHelperText}
            />
            {/* POM-6144 — how much of the addition accounts for stock already dispensed from
                this lot. Shown only where it can mean something: a claim to settle, and a reason
                code that credits stock (a write-off closes the claim on the way OUT instead).

                READ-ONLY for now. It reports what will happen rather than asking; the value is
                still sent, so core settles exactly as before. */}
            {canSettleOnAdd && addActive && (
              <TextField
                type='number'
                label='Already dispensed'
                size='small'
                disabled
                sx={{ width: 180 }}
                value={String(settledQtyNum)}
                inputProps={{ readOnly: true, 'data-testid': 'adjust-settled' }}
                helperText={`of this lot's ${unverifiedQty} ${SYSTEM_DEFICIT.toLowerCase()}`}
              />
            )}
          </Box>
          {canSettleOnAdd && addActive && (
            <Typography
              variant='caption'
              color='text.secondary'
              sx={{ display: 'block', mt: -0.75, mb: 1.5 }}
              data-testid='adjust-settle-summary'
            >
              {`Adds ${addQtyNum}, of which ${settledQtyNum} was already dispensed — remaining goes up by ${
                addQtyNum - settledQtyNum
              } and the ${SYSTEM_DEFICIT.toLowerCase()} balance drops to ${unverifiedQty - settledQtyNum}.`}
            </Typography>
          )}
          <TextField
            label='Notes *'
            size='small'
            fullWidth
            multiline
            rows={2}
            value={addNotes}
            onChange={(e) => setAddNotes(e.target.value)}
            error={submitted && addActive && addNotes.trim().length === 0}
            helperText={
              submitted && addActive && addNotes.trim().length === 0
                ? 'Notes are required for stock additions'
                : ''
            }
          />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              size='small'
              color='inherit'
              sx={{ textTransform: 'none', color: 'text.secondary' }}
              onClick={handleClearAdd}
            >
              Clear
            </Button>
          </Box>
        </AccordionSection>

        {/* Remove Stock accordion */}
        <AccordionSection
          expanded={removeExpanded}
          onToggle={() => setRemoveExpanded((prev) => !prev)}
          icon={
            <RemoveCircleOutlineIcon sx={{ color: 'warning.main', fontSize: 20, flexShrink: 0 }} />
          }
          title='Remove Stock'
          subtitle='deduct with reasons'
          accentColor='#e65100'
          status={removeStatus}
        >
          <TextField
            type='number'
            label='Qty to Remove *'
            size='small'
            sx={{ width: 180, mb: 1.5 }}
            value={removeTotal}
            onChange={(e) => setRemoveTotal(e.target.value)}
            inputProps={{ min: 1, step: 1 }}
            error={removeTotalIsDecimal || removeQtyOverLimit}
            helperText={removeHelperText}
          />
          <TextField
            label='Notes'
            size='small'
            fullWidth
            multiline
            rows={2}
            value={removeNotes}
            onChange={(e) => setRemoveNotes(e.target.value)}
            sx={{ mb: 1.5 }}
          />
          <Box>
            <Typography variant='caption' sx={{ fontWeight: 600, display: 'block', mb: 0.75 }}>
              Removal Reasons *
            </Typography>
            <RemoveReasonRows
              rows={removeReasons}
              onChange={setRemoveReasons}
              totalQty={removeTotal}
              submitted={submitted && removeActive}
            />
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
            <Button
              size='small'
              color='inherit'
              sx={{ textTransform: 'none', color: 'text.secondary' }}
              onClick={handleClearRemove}
            >
              Clear
            </Button>
          </Box>
        </AccordionSection>

        {showBothWarning && (
          <Alert severity='info' sx={{ py: 0.5 }}>
            Both an addition and a removal will be saved as separate audit transactions.
          </Alert>
        )}
      </Box>

      {/* ── Footer ── */}
      <DialogActions
        sx={{
          px: 2.5,
          py: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          justifyContent: 'space-between',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            size='small'
            variant='text'
            color='info'
            startIcon={<InfoOutlinedIcon />}
            sx={{ textTransform: 'none' }}
            onMouseEnter={(e) => setHowItWorksAnchor(e.currentTarget)}
            onMouseLeave={() => setHowItWorksAnchor(null)}
          >
            How it works
          </Button>
          <Popover
            open={Boolean(howItWorksAnchor)}
            anchorEl={howItWorksAnchor}
            onClose={() => setHowItWorksAnchor(null)}
            anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
            transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            disableRestoreFocus
            sx={{ pointerEvents: 'none' }}
            slotProps={{
              paper: {
                sx: {
                  maxWidth: 440,
                  borderLeft: '4px solid',
                  borderColor: 'info.main',
                  p: 2,
                  pointerEvents: 'none',
                },
              },
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1 }}>
              <InfoOutlinedIcon fontSize='small' sx={{ color: 'info.main' }} />
              <Typography variant='subtitle2' sx={{ fontWeight: 700 }}>
                How it works
              </Typography>
            </Box>
            <Typography
              variant='body2'
              color='text.secondary'
              component='ol'
              sx={{ m: 0, pl: 2.5 }}
            >
              <li style={{ marginBottom: 6 }}>
                {/* eslint-disable-next-line max-len */}
                Select <strong>Add Stock</strong> to increase remaining qty — use for count
                corrections, returns, or found discrepancies. Notes are required.
              </li>
              <li style={{ marginBottom: 6 }}>
                {/* eslint-disable-next-line max-len */}
                Select <strong>Remove Stock</strong> to reduce remaining qty. Assign removal reasons
                per quantity group. WIP-allocated units cannot be removed.
              </li>
              <li style={{ marginBottom: 6 }}>
                {/* eslint-disable-next-line max-len */}
                <strong>Available Qty</strong> = Remaining minus WIP. This is the maximum quantity
                you can remove.
              </li>
              <li>
                {/* eslint-disable-next-line max-len */}
                Both an addition and a removal can be saved together — they are recorded as separate
                audit transactions with your name and timestamp.
              </li>
            </Typography>
          </Popover>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={handleClose} color='inherit'>
            Cancel
          </Button>
          <Button
            variant='contained'
            onClick={handleSave}
            disabled={!canSave || saving || lockedError}
            endIcon={
              saving ? (
                <CircularProgress size={14} color='inherit' />
              ) : (
                <ArrowForwardIcon fontSize='small' />
              )
            }
          >
            {saving ? 'Saving…' : 'Save Adjustment'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

ManualAdjustmentDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  lot: PropTypes.shape({
    inventoryLotId: PropTypes.string,
    lotNumber: PropTypes.string,
    expirationDate: PropTypes.string,
    remainingQty: PropTypes.number,
    availableQty: PropTypes.number,
    wipQty: PropTypes.number,
    // POM-6111 — the unverified (negative) balance; string or number depending on the caller.
    unverifiedQty: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    daysUntilExpiry: PropTypes.number,
    isExpired: PropTypes.bool,
    isExpiringSoon: PropTypes.bool,
    firstReceivedAt: PropTypes.string,
    lastMovementAt: PropTypes.string,
  }),
  drugName: PropTypes.string,
  strength: PropTypes.string,
  ndc: PropTypes.string,
  fulfillingPharmacyId: PropTypes.string,
  facilityName: PropTypes.string,
  onSaveSuccess: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};
ManualAdjustmentDialog.defaultProps = {
  lot: null,
  drugName: '',
  strength: '',
  ndc: '',
  fulfillingPharmacyId: '',
  facilityName: '',
  onSaveSuccess: () => {},
};

export default ManualAdjustmentDialog;
